/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
/*  This file creates the details per stage that we show in our voltage range view
    If number of stages = n, then for the component selection view, this object will get created n times.  For the tolerance/next steps view
    this object will be created 2n times: we create a nominal and worst case version for each stage.

    Because this object is created separately for nominal and worst case, there should be no differentiation between worst case and nominal in this file.
    All differentiation between nominal and worst case should be handled in voltage-range.js

*/
import EmberObject, { computed } from '@ember/object';

import Rate from './voltage-range/rate';
import Signal from './voltage-range/signal';
import * as CommonUtils from '../../../utils/common';

export default class VoltageRangeDetails extends EmberObject {
  stage = null;
  smallestMultiplier = null;
  smallestMultiplierWorstCase = null;
  vRange = null;
  vRangeWorstCase = null;
  whoAmI = 'app/models/results/sub-results/voltage-range-detail.js';
  opAmp;
  opAmpDetails;
  stageLetter; // string
  vsPlus = null;
  vsMinus = null;
  init() {
    if (!CommonUtils.isNullOrUndefined(this.stage)) {
      this.opAmp = this.stage.circuit.opAmp.partNumber;
      this.opAmpDetails = this.stage.circuit.opAmp;
      this.stageLetter = this.stage.targetStage.stageLetter;
    }
  }

  @computed('smallestMultiplier', 'vRange') get warning() {
    var smallestMultiplier = this.smallestMultiplier;
    var multiplierSignalInput = this.vRange.multiplierSignalInput;
    var multiplierSignalOutput = this.vRange.multiplierSignalOutput;
    var multiplierSignalSlewRate = this.vRange.multiplierSignalSlewRate;
    var result = { slewRate: false, input: false, output: false };

    if (smallestMultiplier === multiplierSignalSlewRate) {
      result.slewRate = true;
    } else if (smallestMultiplier === multiplierSignalOutput) {
      result.output = true;
    } else if (smallestMultiplier === multiplierSignalInput) {
      result.input = true;
    }

    return result;
  }

  @computed('vRange', 'smallestMultiplier') get maxInputSignal() {
    var vRange = this.vRange;
    var maxSignalInput = vRange.maxSignalInput;
    var smallestMultiplier = this.smallestMultiplier;
    var result = smallestMultiplier * maxSignalInput * 2;
    return result;
  }

  @computed('warning', 'vRange') get amplifierSpecs() {
    var warning = this.warning;
    var vRange = this.vRange;
    var stage = this.stage;
    var opAmpSlewRate = stage.circuit.opAmp.slewRate;
    var minimumInput = vRange.minInput;
    var slewRate = Rate.create({ value: opAmpSlewRate, frequency: null, warningEnabled: warning.slewRate });
    var inputRange = Signal.create({ minimum: minimumInput, maximum: vRange.maxInput, warningEnabled: warning.input });
    var outputRange = Signal.create({
      minimum: vRange.minOutput,
      maximum: vRange.maxOutput,
      warningEnabled: warning.output,
    });
    var result = { slewRate: slewRate, inputRange: inputRange, outputRange: outputRange };
    return result;
  }

  @computed('warning', 'smallestMultiplier', 'vRange', 'refV') get maxSignals() {
    var warning = this.warning;
    var vRange = this.vRange;
    var smallestMultiplier = this.smallestMultiplier;
    var refV = vRange.refV;
    var result = this.computeMaxSignals(warning, smallestMultiplier, vRange, refV);
    return result;
  }

  computeMaxSignals(warning, smallestMultiplier, vRange, refV) {
    var maxSignalInput = vRange.maxSignalInput;
    var maxSignalOutput = vRange.maxSignalOutput;

    var slewRateValue = vRange.maxSignalSlewRate * smallestMultiplier;
    var slewRateFrequency = vRange.maxSignalSlewRateFrequency;
    var slewRate = Rate.create({
      value: slewRateValue,
      frequency: slewRateFrequency,
      warningEnabled: warning.slewRate,
    });

    var minimumInput = refV + -1 * maxSignalInput * smallestMultiplier;
    var maximumInput = smallestMultiplier * maxSignalInput + refV;
    var inputRangeFrequency = vRange.maxSignalInputFrequency;
    var inputRange = Signal.create({
      frequency: inputRangeFrequency,
      minimum: minimumInput,
      maximum: maximumInput,
      warningEnabled: warning.input,
    });

    var minimumOutput = refV + -1 * maxSignalOutput * smallestMultiplier;
    var maximumOutput = smallestMultiplier * maxSignalOutput + refV;
    var outputRangeFrequency = vRange.maxSignalOutputFrequency;
    var outputRange = Signal.create({
      frequency: outputRangeFrequency,
      minimum: minimumOutput,
      maximum: maximumOutput,
      warningEnabled: warning.output,
    });

    var result = { slewRate: slewRate, inputRange: inputRange, outputRange: outputRange };
    return result;
  }
}
